import { ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ArrowBack, ChevronLeft, ChevronRight } from "@mui/icons-material";
import {
  ArchiveDescription as ArchiveDoc,
  getArchiveDescription,
  getMonthDescription,
  isCrossSet,
  MonthDescription,
} from "../api/archive";
import { extractErrorMessage } from "../api/client";
import { YearCalendar } from "../components/archive/YearCalendar";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const fmt = (n: number | null | undefined): string =>
  n === null || n === undefined ? "—" : Math.round(n).toLocaleString();

const compact = (n: number): string =>
  new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 }).format(
    Math.round(n),
  );

// The year-scoped "what changed" timeline (arrivals/departures/re-keys/
// outages) is hidden until those auto-detected signals are validated —
// same rationale as the main page's station-activity section.
const SHOW_WHAT_CHANGED = false;

export function ArchiveYear() {
  const { year = "" } = useParams();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  // The corpus is carried in the URL so the year drill-down stays on the
  // same content type the user picked on the description page. The cross-set
  // ("all") view has no per-year drill-down, so this is always broadcast or
  // podcast.
  const contentType = sp.get("content_type") || "broadcast";
  const ctQs = contentType !== "broadcast" ? `?content_type=${contentType}` : "";
  const [assembled, setAssembled] = useState<ArchiveDoc | null>(null);
  const [months, setMonths] = useState<MonthDescription[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setAssembled(null);
    setMonths(null);
    setError(null);

    getArchiveDescription(contentType)
      .then((d) =>
        isCrossSet(d)
          ? setError("the per-year view is only available per corpus")
          : setAssembled(d),
      )
      .catch((e) => setError(`archive load failed: ${extractErrorMessage(e)}`));

    Promise.allSettled(
      Array.from({ length: 12 }, (_, i) =>
        getMonthDescription(year, String(i + 1).padStart(2, "0"), contentType),
      ),
    ).then((results) => {
      const ok = results
        .filter((r): r is PromiseFulfilledResult<MonthDescription> => r.status === "fulfilled")
        .map((r) => r.value);
      setMonths(ok);
    });
  }, [year, contentType]);

  // Which years exist (for prev/next nav), from the assembled timeline.
  const years = useMemo(() => {
    const s = new Set<string>();
    (assembled?.timeline ?? []).forEach((t) => s.add(t.month.slice(0, 4)));
    return [...s].sort();
  }, [assembled]);
  const yi = years.indexOf(year);
  const prevYear = yi > 0 ? years[yi - 1] : null;
  const nextYear = yi >= 0 && yi < years.length - 1 ? years[yi + 1] : null;

  // Per-month timeline rows for this year (summary stats, always present
  // even before the heavier month docs land).
  const yearTimeline = useMemo(
    () => (assembled?.timeline ?? []).filter((t) => t.month.startsWith(`${year}-`)),
    [assembled, year],
  );

  const agg = useMemo(() => aggregate(months ?? []), [months]);

  // Year-scoped lifecycle events (arrivals/departures/re-keys/outages).
  const events = useMemo(
    () => (assembled ? yearEvents(assembled, year) : []),
    [assembled, year],
  );
  const errata = useMemo(
    () => (assembled?.errata ?? []).filter((e) => String(e.year) === year),
    [assembled, year],
  );

  if (error && !assembled) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }
  if (!assembled) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <CircularProgress size={20} />
      </Container>
    );
  }

  const pages = yearTimeline.reduce((a, t) => a + t.pages, 0);
  const hours = yearTimeline.reduce((a, t) => a + t.duration_hours, 0);
  const daysPresent = yearTimeline.reduce((a, t) => a + (t.days_present ?? 0), 0);
  const daysTotal = yearTimeline.reduce((a, t) => a + (t.days_in_month ?? 0), 0);
  const covPct = daysTotal > 0 ? Math.round((daysPresent / daysTotal) * 100) : null;
  const gapDays = daysTotal - daysPresent;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Button
        startIcon={<ArrowBack />}
        size="small"
        onClick={() => navigate(`/admin/description${ctQs}`)}
      >
        Archive description
      </Button>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 1, mb: 2 }}
      >
        <Typography variant="h4">{year} in depth</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            startIcon={<ChevronLeft />}
            disabled={!prevYear}
            onClick={() => prevYear && navigate(`/admin/description/${prevYear}${ctQs}`)}
          >
            {prevYear ?? "—"}
          </Button>
          <Button
            size="small"
            endIcon={<ChevronRight />}
            disabled={!nextYear}
            onClick={() => nextYear && navigate(`/admin/description/${nextYear}${ctQs}`)}
          >
            {nextYear ?? "—"}
          </Button>
        </Stack>
      </Stack>

      {/* Year metric boxes */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }}>
        <Metric label="Pages" value={compact(pages)} />
        <Metric label="Broadcast hours" value={compact(hours)} />
        <Metric
          label="Sources"
          value={agg.sources > 0 ? compact(agg.sources) : "…"}
          hint="distinct stations this year"
        />
        <Metric label="Coverage" value={covPct === null ? "—" : `${covPct}%`} hint={`${gapDays} gap days`} />
      </Stack>

      {/* Daily capture calendar — the centerpiece */}
      <SectionTitle>Daily capture</SectionTitle>
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        {months === null ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={20} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              loading the year's daily detail…
            </Typography>
          </Stack>
        ) : months.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No per-month detail cached for {year} yet.
          </Typography>
        ) : (
          <>
            <YearCalendar year={year} months={months} />
            <CalendarLegend />
          </>
        )}
      </Paper>

      {/* Months */}
      <SectionTitle>Months</SectionTitle>
      <Paper variant="outlined" sx={{ mb: 3, overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>month</TableCell>
              <TableCell align="right">pages</TableCell>
              <TableCell align="right">hours</TableCell>
              <TableCell align="right">sources</TableCell>
              <TableCell align="right">days</TableCell>
              <TableCell align="right">gaps</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {yearTimeline.map((t) => {
              const mi = Number(t.month.slice(5, 7)) - 1;
              const gaps = (t.days_in_month ?? 0) - (t.days_present ?? 0);
              return (
                <TableRow key={t.month} hover>
                  <TableCell>{MONTH_NAMES[mi] ?? t.month}</TableCell>
                  <TableCell align="right">{fmt(t.pages)}</TableCell>
                  <TableCell align="right">{fmt(t.duration_hours)}</TableCell>
                  <TableCell align="right">{fmt(t.distinct_stations)}</TableCell>
                  <TableCell align="right">
                    {t.days_present ?? "—"}/{t.days_in_month ?? "—"}
                  </TableCell>
                  <TableCell align="right">
                    {gaps > 0 ? (
                      <Typography variant="body2" color="error">
                        {gaps}
                      </Typography>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {/* Top networks this year */}
      <SectionTitle>Top networks{months && months.length ? "" : ""}</SectionTitle>
      <Paper variant="outlined" sx={{ mb: 3, overflowX: "auto" }}>
        {months === null ? (
          <LinearProgress />
        ) : agg.networks.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No network detail cached for {year}.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>station</TableCell>
                <TableCell align="right">pages</TableCell>
                <TableCell align="right">hours</TableCell>
                <TableCell align="right">months present</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agg.networks.slice(0, 30).map((n) => (
                <TableRow key={n.guid} hover>
                  <TableCell>{n.name ?? n.guid}</TableCell>
                  <TableCell align="right">{fmt(n.pages)}</TableCell>
                  <TableCell align="right">{fmt(n.hours)}</TableCell>
                  <TableCell align="right">{n.months}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Categories + media types */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 3 }}>
        <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Categories
          </Typography>
          {agg.categories.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {months === null ? "loading…" : "none"}
            </Typography>
          ) : (
            <ShareBars
              items={agg.categories.map((c) => ({ label: c.label, value: c.pages }))}
              total={agg.catTotal}
            />
          )}
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Media types
          </Typography>
          {agg.media.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {months === null ? "loading…" : "none"}
            </Typography>
          ) : (
            <ShareBars
              items={agg.media.map((m) => ({ label: m.label, value: m.value }))}
              total={agg.mediaTotal}
            />
          )}
        </Paper>
      </Stack>

      {/* What changed this year — hidden until the auto-detected station
          activity (re-keys / outages) is validated. */}
      {SHOW_WHAT_CHANGED && (
        <>
          <SectionTitle>What changed in {year}</SectionTitle>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            {events.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No station arrivals, departures, re-keys, or outages recorded in {year}.
              </Typography>
            ) : (
              <Stack
                spacing={0.75}
                divider={<Divider flexItem />}
                sx={{ maxHeight: 360, overflowY: "auto" }}
              >
                {events.map((e, i) => (
                  <Stack key={i} direction="row" spacing={1.5} alignItems="baseline">
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 56 }}>
                      {e.when}
                    </Typography>
                    <Chip label={e.tag} size="small" color={e.color} variant="outlined" />
                    <Typography variant="body2">{e.text}</Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Paper>
        </>
      )}

      {/* Errata */}
      <SectionTitle>Errata</SectionTitle>
      <Paper variant="outlined" sx={{ p: 2 }}>
        {errata.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No errata recorded for {year}.
          </Typography>
        ) : (
          <Stack spacing={1} divider={<Divider flexItem />}>
            {errata.map((e) => (
              <Box key={`${e.year}-${e.month}`}>
                <Typography variant="subtitle2">
                  {MONTH_NAMES[Number(e.month) - 1] ?? e.month}
                </Typography>
                {e.notes.map((note, i) => (
                  <Typography key={i} variant="body2" color="text.secondary">
                    • {note.text}
                  </Typography>
                ))}
              </Box>
            ))}
          </Stack>
        )}
      </Paper>
    </Container>
  );
}

// ---- small presentational helpers ----------------------------------------

function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <Typography variant="overline" color="text.secondary" sx={{ display: "block", mt: 1, mb: 1 }}>
      {children}
    </Typography>
  );
}

function Metric({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5">{value}</Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Paper>
  );
}

function ShareBars({ items, total }: { items: { label: string; value: number }[]; total: number }) {
  const top = [...items].sort((a, b) => b.value - a.value).slice(0, 8);
  return (
    <Stack spacing={0.75}>
      {top.map((it) => {
        const pct = total > 0 ? (it.value / total) * 100 : 0;
        return (
          <Box key={it.label}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">{it.label}</Typography>
              <Typography variant="caption" color="text.secondary">
                {pct.toFixed(0)}%
              </Typography>
            </Stack>
            <LinearProgress variant="determinate" value={Math.min(100, pct)} sx={{ height: 6, borderRadius: 1 }} />
          </Box>
        );
      })}
    </Stack>
  );
}

function CalendarLegend() {
  return (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1.5, flexWrap: "wrap" }}>
      <Typography variant="caption" color="text.secondary">
        each cell = one day · shade = page volume · red = no capture · gray = no cached data
      </Typography>
    </Stack>
  );
}

// ---- aggregation over the year's month docs -------------------------------

interface YearAgg {
  sources: number;
  networks: { guid: string; name: string | null; pages: number; hours: number; months: number }[];
  categories: { label: string; pages: number }[];
  catTotal: number;
  media: { label: string; value: number }[];
  mediaTotal: number;
}

function aggregate(months: MonthDescription[]): YearAgg {
  const sources = new Set<string>();
  const nets = new Map<string, { name: string | null; pages: number; hours: number; months: number }>();
  const cats = new Map<string, number>();
  const media = new Map<string, number>();

  for (const d of months) {
    for (const r of d.station_roster ?? []) sources.add(r.source_id);
    for (const n of d.by_network ?? []) {
      const cur = nets.get(n.source_id) ?? { name: n.source_name, pages: 0, hours: 0, months: 0 };
      cur.pages += n.pages ?? 0;
      cur.hours += n.duration_hours ?? 0;
      cur.months += 1;
      if (n.source_name) cur.name = n.source_name;
      nets.set(n.source_id, cur);
    }
    for (const c of d.by_category ?? []) {
      const k = c.category ?? "uncategorized";
      cats.set(k, (cats.get(k) ?? 0) + (c.pages ?? 0));
    }
    for (const [mt, count] of Object.entries(d.by_media_type ?? {})) {
      media.set(mt, (media.get(mt) ?? 0) + count);
    }
  }

  const networks = [...nets.entries()]
    .map(([guid, v]) => ({ guid, ...v }))
    .sort((a, b) => b.pages - a.pages);
  const categories = [...cats.entries()].map(([label, pages]) => ({ label, pages }));
  const catTotal = categories.reduce((a, c) => a + c.pages, 0);
  const mediaArr = [...media.entries()].map(([label, value]) => ({ label, value }));
  const mediaTotal = mediaArr.reduce((a, m) => a + m.value, 0);

  return { sources: sources.size, networks, categories, catTotal, media: mediaArr, mediaTotal };
}

// ---- year-scoped lifecycle events -----------------------------------------

type EventColor = "success" | "default" | "warning" | "error";
interface YearEvent {
  when: string; // "YYYY-MM"
  tag: string;
  text: string;
  color: EventColor;
}

function yearEvents(doc: ArchiveDoc, year: string): YearEvent[] {
  const out: YearEvent[] = [];
  const inYear = (m: string | undefined) => (m ?? "").startsWith(`${year}-`);
  const ar = doc.auto_remarks;

  for (const a of ar.arrivals) {
    if (inYear(a.month)) out.push({ when: a.month, tag: "arrived", text: a.text, color: "success" });
  }
  for (const d of ar.departures) {
    if (inYear(d.month)) out.push({ when: d.month, tag: "departed", text: d.text, color: "default" });
  }
  for (const r of ar.rekeys) {
    if (inYear(r.month)) out.push({ when: r.month, tag: "re-keyed", text: r.text, color: "default" });
  }
  for (const o of ar.outages) {
    const ws = (o.start ?? "").slice(0, 7);
    const we = (o.end ?? "").slice(0, 7);
    if (ws.startsWith(year) || we.startsWith(year)) {
      out.push({ when: ws, tag: "outage", text: o.text, color: "warning" });
    }
  }
  return out.sort((a, b) => a.when.localeCompare(b.when));
}
